import React, { useState } from 'react';
import PropTypes from 'prop-types';

import AddAllButton from './AddAllButton';
import ExpandAccordionButton from './ExpandAccordianButton';
import PromoteSiteItem from './PromoteSiteItem';

const PromoteSiteList = ({
  sitesList,
  currentSiteId,
  handleAddSite,
  handleRemoveSite,
  handleAddMultipleSites,
}) => {
  const [showBelowFold, setShowBelowFold] = useState(
    // initially show below fold if any are already selected
    sitesList.some((site) => (site.site_id !== currentSiteId && !site.suggested && site.selected)),
  );
  const currentSite = sitesList.find((site) => site.site_id === currentSiteId);
  // Filter out current site and return suggested sites in list form
  const suggestedSites = (() => {
    const nonCurrentSuggestedSites = (
      sitesList
        .filter((site) => (site.site_id !== currentSiteId && site.suggested))
        .toSorted((a, b) => a.order - b.order)
    );
    return [...nonCurrentSuggestedSites];
  })();

  // Return suggested site items
  const suggestedSiteItems = suggestedSites.map(
    (site) => (
      <PromoteSiteItem
        key={site.site_id}
        site={site}
        handleDiveSelect={handleAddSite}
        handleDiveDeselect={handleRemoveSite}
      />
    ),
  );

  // return true if all suggested sites are selected
  const allSuggestedSitesSelected = () => {
    return suggestedSites.every((site) => site.selected);
  };

  // Select all suggested sites
  const selectSuggestedSites = () => {
    // get ids of all sites we want to select
    const siteIds = suggestedSites.map((site) => site.site_id);
    return handleAddMultipleSites(siteIds);
  };

  // Filter out current site and return not suggested sites
  const notSuggestedSiteItems = sitesList
    .filter((site) => { return site.site_id !== currentSiteId && !site.suggested; })
    .toSorted((a, b) => a.site_name.localeCompare(b.site_name)) // sort by site_name alphabetically
    .map((site) => (
      <PromoteSiteItem
        key={site.site_id}
        site={site}
        handleDiveSelect={handleAddSite}
        handleDiveDeselect={handleRemoveSite}
      />
    ));

  return (
    <>
      <div className="promote-items-list">
        {/* Always display the currentSite */}
        <PromoteSiteItem
          key={currentSite.site_id}
          site={currentSite}
          handleDiveSelect={handleAddSite}
          handleDiveDeselect={handleRemoveSite}
        />
        {suggestedSiteItems.length > 0
          // If there are suggested sites, display aboveTheFold with the currentSite
          ? (
            <div>
              {suggestedSiteItems}
              <AddAllButton
                selectSuggestedSites={selectSuggestedSites}
                allSuggestedSitesSelected={allSuggestedSitesSelected}
              />
            </div>
          )
          // If there are not suggested sites, display the
          // first seven notSuggestedSites aboveTheFold
          : (
            <div>
              {notSuggestedSiteItems.slice(0, 7)}
            </div>
          )}
        <ExpandAccordionButton
          showBelowFold={showBelowFold}
          setShowBelowFold={setShowBelowFold}
        />
        {suggestedSiteItems.length > 0
          // Conditional CSS class to toggle display is used here because if we use
          // conditional rendering of the belowTheFold items the 'checked' state is
          // not preserved when the component is re-rendered.
          // If there are suggestedSites, display all notSuggestedSites below the fold
          ? (
            <div className={!showBelowFold ? 'site-item-display' : null}>
              {notSuggestedSiteItems}
            </div>
          )
          // If there are not suggestedSites, display notSuggestedSites from index 8 onwards since
          // the first 7 in this array were displayed aboveTheFold
          : (
            <div className={!showBelowFold ? 'site-item-display' : null}>
              {notSuggestedSiteItems.slice(8)}
            </div>
          )}
      </div>
    </>
  );
};

PromoteSiteList.propTypes = {
  sitesList: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      site_name: PropTypes.string.isRequired,
      site_domain: PropTypes.string.isRequired,
      site_id: PropTypes.number.isRequired,
      order: PropTypes.number,
      suggested: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  currentSiteId: PropTypes.number.isRequired,
  handleAddSite: PropTypes.func.isRequired,
  handleRemoveSite: PropTypes.func.isRequired,
  handleAddMultipleSites: PropTypes.func.isRequired,
};

export default PromoteSiteList;
